import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Save, Mail } from "lucide-react";

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [tables, setTables] = useState([]);
  const [tDlg, setTDlg] = useState(null);

  const loadTables = () => api.get("/tables").then((r) => setTables(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
    loadTables();
  }, []);

  const saveSettings = async () => {
    try {
      const payload = { ...s, booking_sources: typeof s.booking_sources === "string" ? s.booking_sources.split(",").map((x) => x.trim()).filter(Boolean) : s.booking_sources };
      await api.put("/settings", payload); toast.success("Настройките са запазени");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveTable = async () => {
    const t = tDlg;
    if (!t.number) return toast.error("Номер е задължителен");
    try {
      const payload = { number: t.number, seats: +t.seats || 4, zone: t.zone || "Основна зала" };
      if (t.id) await api.put(`/tables/${t.id}`, payload); else await api.post("/tables", payload);
      toast.success("Записано"); setTDlg(null); loadTables();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delTable = async (id) => { if (!window.confirm("Изтриване на масата?")) return; await api.delete(`/tables/${id}`); loadTables(); };

  const sendDailyReport = async () => {
    try { await api.post("/reports/email-daily"); toast.success("Дневният отчет е изпратен"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!s) return <div className="p-6 text-muted-foreground">Зареждане…</div>;

  return (
    <div className="p-6">
      <h1 className="font-display text-4xl text-forest-deep">Настройки</h1>
      <div className="gold-rule w-24 my-3" />
      <Tabs defaultValue="business">
        <TabsList className="bg-cream-muted">
          <TabsTrigger value="business" data-testid="tab-business">Бизнес</TabsTrigger>
          <TabsTrigger value="tables" data-testid="tab-tables">Маси</TabsTrigger>
        </TabsList>

        <TabsContent value="business">
          <div className="bg-white rounded-2xl border border-gold/10 shadow-soft p-6 mt-4 max-w-2xl space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label className="text-xs">Име (BG)</Label><Input value={s.name || ""} onChange={(e) => setS({ ...s, name: e.target.value })} data-testid="set-name" /></div>
              <div><Label className="text-xs">Име (EN)</Label><Input value={s.name_en || ""} onChange={(e) => setS({ ...s, name_en: e.target.value })} /></div>
              <div><Label className="text-xs">Адрес</Label><Input value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} /></div>
              <div><Label className="text-xs">Телефон</Label><Input value={s.phone || ""} onChange={(e) => setS({ ...s, phone: e.target.value })} /></div>
              <div><Label className="text-xs">Имейл</Label><Input value={s.email || ""} onChange={(e) => setS({ ...s, email: e.target.value })} /></div>
              <div><Label className="text-xs">Имейл за известия (резервации/отчети)</Label><Input value={s.notification_email || ""} onChange={(e) => setS({ ...s, notification_email: e.target.value })} data-testid="set-notification-email" placeholder="owner@example.com" /></div>
              <div><Label className="text-xs">Работно време</Label><Input value={s.restaurant_hours || ""} onChange={(e) => setS({ ...s, restaurant_hours: e.target.value })} /></div>
              <div><Label className="text-xs">Валута</Label><Input value={s.currency || "EUR"} onChange={(e) => setS({ ...s, currency: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">Източници на резервации (разделени със запетая)</Label>
              <Input value={Array.isArray(s.booking_sources) ? s.booking_sources.join(", ") : (s.booking_sources || "")} onChange={(e) => setS({ ...s, booking_sources: e.target.value })} /></div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={saveSettings} className="bg-forest text-white rounded-xl gap-2" data-testid="save-settings"><Save className="w-4 h-4" /> Запази</Button>
              <Button variant="outline" onClick={sendDailyReport} className="rounded-xl gap-2" data-testid="send-daily-report"><Mail className="w-4 h-4" /> Изпрати дневен отчет сега</Button>
            </div>
            <p className="text-xs text-muted-foreground">Дневният отчет се изпраща автоматично всяка вечер в 21:30 на посочения имейл за известия.</p>
          </div>
        </TabsContent>

        <TabsContent value="tables">
          <div className="flex justify-end mt-4"><Button onClick={() => setTDlg({ number: "", seats: 4, zone: "Основна зала" })} className="rounded-xl gap-2 bg-forest text-white" data-testid="add-table-btn"><Plus className="w-4 h-4" /> Нова маса</Button></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-4">
            {tables.map((t) => (
              <div key={t.id} className="bg-white rounded-2xl border border-gold/10 shadow-soft p-4" data-testid={`settings-table-${t.number}`}>
                <div className="font-display text-2xl text-forest-deep">Маса {t.number}</div>
                <div className="text-xs text-muted-foreground">{t.zone} · {t.seats} места</div>
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setTDlg(t)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="outline" size="icon" className="h-8 w-8 text-destructive" onClick={() => delTable(t.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!tDlg} onOpenChange={(o) => !o && setTDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{tDlg?.id ? "Редактиране на маса" : "Нова маса"}</DialogTitle></DialogHeader>
          {tDlg && (
            <div className="space-y-3">
              <div><Label className="text-xs">Номер</Label><Input value={tDlg.number} onChange={(e) => setTDlg({ ...tDlg, number: e.target.value })} data-testid="table-number-input" /></div>
              <div><Label className="text-xs">Места</Label><Input type="number" value={tDlg.seats} onChange={(e) => setTDlg({ ...tDlg, seats: e.target.value })} /></div>
              <div><Label className="text-xs">Зона</Label><Input value={tDlg.zone} onChange={(e) => setTDlg({ ...tDlg, zone: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveTable} className="bg-forest text-white rounded-xl" data-testid="save-table">Запази</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
